import { createElement } from 'react';
import { Components, Theme } from '@mui/material/styles';
import { KeyboardArrowDownRounded as ChevronDownIcon } from '@mui/icons-material';
import { AppColorTokens, BRAND_GRADIENT } from './appColorTokens';

// Flat, shadcn-style controls instead of stock Material: no ripples, static labels above
// 40px inputs (no floating label or notched outline), soft focus rings, compact menus.
export const formControlOverrides = (t: AppColorTokens): Components<Theme> => {
  const focusRing = { outline: `2px solid ${t.primary}`, outlineOffset: 2 };
  const popupPaper = {
    marginTop: 4,
    border: `1px solid ${t.border}`,
    borderRadius: 10,
    backgroundColor: t.card,
    boxShadow: '0 8px 24px rgba(0,0,0,.12)',
  };
  const listItem = { borderRadius: 6, fontSize: 14, minHeight: 34 };

  return {
    MuiButtonBase: {
      defaultProps: { disableRipple: true, disableTouchRipple: true },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: '0.875rem',
          height: 36,
          paddingInline: 14,
          whiteSpace: 'nowrap',
          transition: 'background-color .15s, border-color .15s, filter .15s',
          '&.Mui-focusVisible': focusRing,
        },
        sizeSmall: { height: 32, paddingInline: 12, fontSize: '0.8125rem' },
        sizeLarge: { height: 42 },
        containedPrimary: {
          background: BRAND_GRADIENT,
          color: '#fff',
          '&:hover': { background: BRAND_GRADIENT, filter: 'brightness(1.05)' },
          '&.Mui-disabled': { background: t.mutedBg, color: t.muted },
        },
        containedError: {
          backgroundColor: t.danger,
          color: '#fff',
          '&:hover': { backgroundColor: t.danger, filter: 'brightness(1.08)' },
          '&.Mui-disabled': { background: t.mutedBg, color: t.muted },
        },
        outlined: {
          borderColor: t.border,
          color: t.fg,
          backgroundColor: t.card,
          '&:hover': { borderColor: t.border, backgroundColor: t.mutedBg },
        },
        outlinedError: { color: t.danger, '&:hover': { backgroundColor: t.dangerBg } },
        text: { color: t.muted, '&:hover': { backgroundColor: t.mutedBg, color: t.fg } },
        textError: { color: t.danger, '&:hover': { backgroundColor: t.dangerBg, color: t.danger } },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover': { backgroundColor: t.mutedBg },
          '&.Mui-focusVisible': focusRing,
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    // Labels always sit above the field, so the label never floats over the input.
    MuiInputLabel: {
      defaultProps: { shrink: true },
      styleOverrides: {
        root: {
          '&.MuiInputLabel-root': {
            position: 'relative',
            transform: 'none',
            marginBottom: 6,
            maxWidth: '100%',
            fontSize: 14,
            fontWeight: 500,
            lineHeight: 1.4,
            color: t.fg,
            pointerEvents: 'auto',
          },
          '&.Mui-focused': { color: t.fg },
          '&.Mui-error': { color: t.danger },
          '&.Mui-disabled': { color: t.muted },
        },
        asterisk: { color: t.danger },
      },
    },
    MuiInputBase: {
      styleOverrides: { root: { fontSize: 14 } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: 40,
          borderRadius: 8,
          backgroundColor: t.card,
          transition: 'box-shadow .15s',
          // Drop the notch cut into the border for the old floating label.
          '& .MuiOutlinedInput-notchedOutline': { top: 0, borderColor: t.border, '& legend': { display: 'none' } },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: t.muted },
          '&.Mui-focused': { boxShadow: `0 0 0 3px ${t.accentBg}` },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: t.primary, borderWidth: 1 },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: t.danger },
          '&.Mui-error.Mui-focused': { boxShadow: `0 0 0 3px ${t.dangerBg}` },
          '&.Mui-disabled': { backgroundColor: t.mutedBg },
        },
        input: { '&::placeholder': { color: t.muted, opacity: 1 } },
      },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { marginInline: 0, marginTop: 6, fontSize: 12 } },
    },
    MuiFormControlLabel: {
      styleOverrides: { label: { fontSize: 14, fontWeight: 500 } },
    },
    MuiSwitch: {
      styleOverrides: {
        root: { width: 36, height: 20, padding: 0, margin: 8 },
        switchBase: {
          padding: 2,
          '&.Mui-checked': { transform: 'translateX(16px)', color: '#fff' },
          '&.Mui-checked + .MuiSwitch-track': { backgroundColor: t.primary, opacity: 1 },
          '&.Mui-focusVisible + .MuiSwitch-track': focusRing,
        },
        thumb: { width: 16, height: 16, boxShadow: 'none', color: '#fff' },
        track: { borderRadius: 10, backgroundColor: t.border, opacity: 1 },
      },
    },
    // Chevron instead of Material's solid triangle; the menu drops below the field
    // instead of opening on top of it with the selected item over the input.
    MuiSelect: {
      defaultProps: {
        IconComponent: ChevronDownIcon,
        MenuProps: {
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
        },
      },
      styleOverrides: {
        icon: { fontSize: 18, right: 10, color: t.muted },
        select: { '&.MuiSelect-select': { paddingRight: 34 } },
      },
    },
    MuiMenu: {
      styleOverrides: { paper: popupPaper, list: { padding: 4 } },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          ...listItem,
          '&:hover': { backgroundColor: t.mutedBg },
          '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: t.accentBg, color: t.accentFg },
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: { popupIcon: createElement(ChevronDownIcon, { sx: { fontSize: 18 } }) },
      styleOverrides: {
        popupIndicator: { color: t.muted },
        paper: popupPaper,
        listbox: {
          padding: 4,
          '& .MuiAutocomplete-option': listItem,
          '& .MuiAutocomplete-option.Mui-focused': { backgroundColor: t.mutedBg },
          '& .MuiAutocomplete-option[aria-selected="true"]': { backgroundColor: t.accentBg },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: '12px 24px 20px', gap: 8, '& > :not(style) ~ :not(style)': { marginLeft: 0 } } },
    },
  };
};
